import React from 'react';

import { styled, StringHelper, Wizard } from '@asicsdigital/oneasics-blocks';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';

import { contentfulApi } from '../../services/api';
import { setPreviewFlag, getLocale, getFilteredQuestion, getAlgoliaIndex } from '../../utils/utils';

const WizardContainer = styled('div', {
  marginLeft: 'auto',
  marginRight: 'auto',
});

const ShoeFinderWizard = ({ sfccData }) => {
  const { questions, wizards, data } = useSelector((state) => state.wizards);
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const noFetch = urlSearchParams.get('noFetch');
  const locales = useParams();
  // const ogImage = data?.ogImage?.url ?? 'https://images.ctfassets.net/q9nezw9z42jm/41irMR326sqZiwir6ce8VF/3dcf00e0e36f4b99e647cc5eed118bbc/asics-running-shoe-finder__2_.png';
  const ogTag1 = data?.ogTag1 ?? '';
  let url = 'http://www.asics.com/us/en-us/shoefinder';

  if (typeof window !== 'undefined') {
    url = window?.location?.href;
  }

  const previewFlag = setPreviewFlag(sfccData);

  const onSubmit = (wizardId) => {
    let orFilters = '';
    let filters = '';
    let gender = '';
    const ruleContexts = [];
    const filteredQuestions = getFilteredQuestion(wizards, wizardId, questions);
    filteredQuestions.forEach((question) => {
      if (question.__typename === 'SizeWidthQuestion') {
        if (question.genderQuestion.answer) {
          
          // add rule context for gender
          ruleContexts.push(`gender-${StringHelper.toSnakeCase(question.genderQuestion.answer)}`);

          // add rule context for width
          ruleContexts.push(`width-${StringHelper.toSnakeCase(question.widthQuestion.answer)}`);

          const sizeWeight = question.sizeWeight ? `<score=${question.sizeWeight}>` : '';
          const widthWeight = question.widthWeight ? `<score=${question.widthWeight}>` : '';

          gender = `gender:"${question.genderQuestion.answer}" AND `;
          filters += `width:"${question.widthQuestion.answer}"${widthWeight} AND `;

          question.sizeQuestion.answers.forEach((answer) => {
            filters += `sizes:"${answer}"${sizeWeight} AND `;
          });
        }
      } else if (question.facet === 'colorPalette' && question.answers.length > 0) {
        const optionalWeight = question.weight ? `<score=${question.weight}>` : '';
        orFilters += `${question.facet}:${question.answers}${optionalWeight}  `;
        question.answers.forEach((ans) => {
          ruleContexts.push(`${question.facet}-${StringHelper.toSnakeCase(ans)}`);
        });
      } else if (question.facet) {
        const optionalWeight = question.weight ? `<score=${question.weight}>` : '';
        if (question.isSingleSelect && question.answer) {
          filters += `${question.facet}:"${question.answer}"${optionalWeight} AND `;
          ruleContexts.push(`${question.facet}-${StringHelper.toSnakeCase(question.answer)}`);
        } else if (question.answers.length > 0) {
          filters += `${question.facet}:"${question.answers}"${optionalWeight} AND `;
          ruleContexts.push(`${question.facet}-${question.answers.map(StringHelper.toSnakeCase).join('_')}`);
        }
      }
    });

    filters = filters.substring(0, filters.length - 5);
    orFilters = orFilters.substring(0, orFilters.length - 2);
    gender = gender.substring(0, gender.length - 5);
    const wizard = wizards[(sfccData?.contentfulWizardId)];

    // define algolia index based on environment
    const algoliaIndex = getAlgoliaIndex(wizard, sfccData);

    const suffix =
      `&numberOfResults=${wizard.numberOfResults}` +
      `&ruleContexts=${ruleContexts}` +
      `&index=${algoliaIndex}` +
      `&resultsPageId=${wizard.resultsPage.sys.id}`;

    const regex = /AND/g;
    const closeMatch = filters.replace(regex, 'OR');

    let urlString = `${sfccData?.resultsUrl}/?filters=${filters}&gender=${gender}`;
    if (orFilters !== '') {
      urlString += `&optionalFilters=${orFilters}`;
    }

    urlString += `${suffix}&closeMatch=${closeMatch}`;
    setTimeout(() => {
      window.location.href = urlString;
    }, 1000);
  };

  return (
    <WizardContainer data-testid="shoefinder">
      {data && (
        <Helmet>
          <meta content={ogTag1} data-react-helmet="true" property="og:tag1" />
          {data?.hidePageFromSearchEngines && <meta content="noindex" data-react-helmet="true" name="robots" />}
          {data?.excludeLinksFormSearchRankings && <meta content="nofollow" data-react-helmet="true" name="robots" />}
        </Helmet>
      )}

      <Helmet>
        {/* <meta content={ogImage} property="og:image"  />
        <meta content={ogImage} property="twitter:image"  /> */}
        <meta content="summary_large_image" data-react-helmet="true" property="twitter:card" />
        <meta content={url} data-react-helmet="true" property="og:url" />
        <meta content={url} data-react-helmet="true" property="twitter:url" />
      </Helmet>

      <Wizard
        contentfulApi={contentfulApi}
        id={sfccData?.contentfulWizardId}
        locale={getLocale(locales)}
        noFetch={noFetch}
        previewFlag={previewFlag}
        onSubmit={onSubmit}
      />
    </WizardContainer>
  );
};

ShoeFinderWizard.propTypes = {
  sfccData: PropTypes.shape({
    component: PropTypes.string,
    contentfulWizardId: PropTypes.string,
    resultsUrl: PropTypes.string,
    sfcc_environment: PropTypes.string,
    wizardUrl: PropTypes.string,
  }),
};

ShoeFinderWizard.defaultProps = {
  sfccData: {},
};

export default ShoeFinderWizard;
